import type { ConversionV2CreationRequest } from '@highereducation/campaign-management';
import type { Result } from '../types/result';
import type { SiteConfig } from '../types/config';
import {
  isStudyMatchResultsSource,
  RESULTS_FORMAT_SUBTYPE,
  UNQUALIFIED_FORMAT_SUBTYPE,
} from './configurable-step/offeringsResultsSourceIdentity';
import getTrafficSource from './getTrafficSource';

type ConversionLocation = {
  country?: string;
  state?: string;
};

export type BuildConversionCreationRequestOptions = {
  flowType?: string;
  trafficSource?: string;
  monetizationChannel?: string;
  formatType?: string;
  formatSubtype?: string;
  location?: ConversionLocation;
};

// Matches class-validator's default `@IsUUID()` (RFC4122 versions 1–5 with a
// valid variant nibble), so the guard only keeps values campaign-management
// will actually accept. A bare 8-4-4-4-12 hex string with a wrong version or
// variant would pass a looser regex here but still 400 on the server.
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * campaign-management v2 validates `trackingContext.tenantId` with
 * class-validator `@IsUUID()` — it must be a UUID (the tenant's tagular
 * `apiKey`, e.g. `src`/Cohesion tenant identity), NOT the tenant slug. The
 * top-level `tenantId` is the slug (CMS resolves the tenant config from it),
 * so the two carry different identifiers. Returns the apiKey only when it is a
 * UUID; otherwise `undefined` so the optional field is omitted rather than
 * sending an invalid value that 400s the whole conversion.
 */
export const resolveTrackingTenantId = (
  siteConfig: SiteConfig,
): string | undefined => {
  const apiKey = siteConfig?.tagularApiKey?.trim();
  return apiKey && UUID_PATTERN.test(apiKey) ? apiKey : undefined;
};

export const buildConversionCreationRequest = (
  selectedResult: Result,
  siteConfig: SiteConfig,
  productClickedCorrelationId: string,
  optionsOrFlowType?: BuildConversionCreationRequestOptions | string,
  formatType?: string,
  formatSubtype?: string,
  location?: ConversionLocation,
): ConversionV2CreationRequest => {
  const offeringId = selectedResult?.offeringId?.trim();
  const tenantId = siteConfig?.tenantId?.trim();
  const options =
    typeof optionsOrFlowType === 'string'
      ? {
          flowType: optionsOrFlowType,
          formatType,
          formatSubtype,
          location,
        }
      : (optionsOrFlowType ??
        (formatType || formatSubtype || location
          ? {
              formatType,
              formatSubtype,
              location,
            }
          : undefined));
  const normalizedFlowType = options?.flowType?.trim();
  const normalizedTrafficSource =
    options?.trafficSource?.trim() ||
    getTrafficSource(window.location.href) ||
    'organic';
  const requestedMonetizationChannel = options?.monetizationChannel?.trim();
  const monetizationChannel = requestedMonetizationChannel
    ? requestedMonetizationChannel
    : 'voyager';

  if (!offeringId) {
    throw new Error('CMS v2 offeringId is required to create a conversion');
  }

  if (!tenantId) {
    throw new Error('CMS v2 tenantId is required to create a conversion');
  }

  const trackingTenantId = resolveTrackingTenantId(siteConfig);
  const defaultSubtype = isStudyMatchResultsSource(
    selectedResult?.resultsSource,
  )
    ? UNQUALIFIED_FORMAT_SUBTYPE
    : RESULTS_FORMAT_SUBTYPE;
  const normalizedLocation = normalizeConversionLocation(options?.location);

  return {
    offeringId,
    tenantId,
    trackingContext: {
      ...(trackingTenantId ? { tenantId: trackingTenantId } : {}),
      formatType: options?.formatType?.trim() || 'dexp-offering',
      formatSubtype: options?.formatSubtype?.trim() || defaultSubtype,
      webContext: {
        anonymousId: (window as any)?._Cohesion?.anonymousId || '',
        fromManual: false,
        instanceId: (window as any)?._Cohesion?.instanceId || '',
        page: {
          referrer:
            (window as any)?._Cohesion?.webContext?.page?.referrer || '',
          title: document.title || '',
          url: window.location.href || '',
        },
        sessionId: (window as any)?._Cohesion?.sessionId || '',
        userAgent: window.navigator.userAgent || '',
      },
    },
    correlationId:
      selectedResult?.productClickedCorrelationId ||
      productClickedCorrelationId ||
      '',
    experience: 'voyager',
    trafficSource: normalizedTrafficSource,
    monetizationChannel,
    ...(normalizedFlowType ? { flowType: normalizedFlowType } : {}),
    ...(normalizedLocation ? { location: normalizedLocation } : {}),
    conversionSubmitMode: 'standard',
  };
};

function normalizeConversionLocation(
  location: ConversionLocation | undefined,
): ConversionLocation | undefined {
  const country = location?.country?.trim().toUpperCase();
  const state = location?.state?.trim().toUpperCase();
  if (!country && !state) {
    return undefined;
  }
  return {
    ...(country ? { country } : {}),
    ...(state ? { state } : {}),
  };
}
